/**
 * NumberInput — wrapper de Input que limpia formato al pegar Y al escribir,
 * y opcionalmente FORMATEA visualmente con separadores de miles mientras se tipea.
 *
 * Iter 232 (Carlos): si el usuario pega `$1.234.567,00` o escribe con puntos, se
 * guarda solo el número crudo (string sin formato). Si `displayFormat=true`, el
 * input MUESTRA `1.234.567` pero el `onChange` recibe `1234567`.
 *
 * Props:
 *  - value: string | number — valor crudo actual (sin separadores).
 *  - onChange: (clean: string) => void — recibe el valor limpio.
 *  - onChangeNumber: (n: number) => void — alternativa que recibe Number.
 *  - allowDecimal: boolean (default true)
 *  - allowNegative: boolean (default true)
 *  - displayFormat: boolean (default true) — separadores de miles visuales.
 *  - displayLocale: string (default 'es-CL') — punto como miles.
 *  - Resto de props: pasan a <Input>.
 *
 * Uso típico:
 *   <NumberInput value={monto} onChange={setMonto} data-testid="monto-input" />
 */
import { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { parseCurrencyInput, makePasteHandler } from '../lib/numericInput';

function formatForDisplay(raw, locale = 'es-CL', allowDecimal = true) {
  if (raw == null || raw === '') return '';
  const s = String(raw);
  // Mientras el usuario termina de escribir "1234,", no agregar separadores aún si hay decimal en proceso.
  const trailingDot = /\.$/.test(s);
  const trailingDigits = s.match(/\.(\d{0,2})$/);
  const negative = s.startsWith('-');
  const abs = negative ? s.slice(1) : s;
  const [intRaw, decRaw = ''] = abs.split('.');
  if (!intRaw && !decRaw) return s; // input vacío "."
  const intNum = intRaw ? parseInt(intRaw, 10) : 0;
  let formatted = '';
  try {
    formatted = new Intl.NumberFormat(locale, { useGrouping: true }).format(intNum);
  } catch {
    formatted = String(intNum);
  }
  if (allowDecimal && (decRaw || trailingDot || trailingDigits)) {
    formatted += ',' + decRaw;
  }
  return (negative ? '-' : '') + formatted;
}

export function NumberInput({
  value,
  onChange,
  onChangeNumber,
  allowDecimal = true,
  allowNegative = true,
  displayFormat = true,
  displayLocale = 'es-CL',
  inputMode = 'decimal',
  onBlur,
  ...rest
}) {
  // El estado interno es el texto mostrado (con formato si displayFormat=true).
  // El value externo es siempre crudo.
  const [displayValue, setDisplayValue] = useState(() => (
    displayFormat ? formatForDisplay(value, displayLocale, allowDecimal) : (value ?? '')
  ));

  // Re-sincroniza si el valor externo cambia desde fuera (ej: reset del formulario).
  useEffect(() => {
    const expected = displayFormat ? formatForDisplay(value, displayLocale, allowDecimal) : (value ?? '');
    // Solo re-sync si no estamos en medio de typing (el valor externo crudo difiere de lo que reconstruiría el display actual).
    const currentClean = parseCurrencyInput(displayValue);
    if (String(currentClean) !== String(value ?? '')) {
      setDisplayValue(expected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (cleanStr) => {
    let v = cleanStr;
    if (!allowDecimal) v = v.replace(/\..*$/, '');
    if (!allowNegative) v = v.replace(/^-/, '');
    onChange?.(v);
    if (onChangeNumber) {
      const n = Number(v);
      onChangeNumber(Number.isFinite(n) ? n : 0);
    }
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setDisplayValue('');
      emit('');
      return;
    }
    // Iter 236.m.17 (Carlos fix): si el usuario acaba de escribir el separador
    // decimal sin dígitos detrás todavía (ej: "1," o "1."), preservar ese
    // separador en el display para que pueda continuar tipeando "1,5" sin
    // que se le borre la coma.
    const hasTrailingSep = allowDecimal && /[.,]$/.test(raw);
    const clean = parseCurrencyInput(raw);
    let display;
    if (displayFormat) {
      display = formatForDisplay(clean, displayLocale, allowDecimal);
      if (hasTrailingSep && !/[.,]$/.test(display)) {
        const decSep = (displayLocale || 'es-CL').startsWith('en') ? '.' : ',';
        display += decSep;
      }
    } else {
      display = clean + (hasTrailingSep ? '.' : '');
    }
    setDisplayValue(display);
    emit(clean);
  };

  const handlePaste = makePasteHandler((clean) => {
    if (displayFormat) {
      setDisplayValue(formatForDisplay(clean, displayLocale, allowDecimal));
    } else {
      setDisplayValue(clean);
    }
    emit(clean);
  });

  const handleBlur = (e) => {
    // Al perder foco, re-formatea limpio (sin punto/coma colgante).
    if (displayFormat && displayValue) {
      const clean = parseCurrencyInput(displayValue);
      setDisplayValue(formatForDisplay(clean, displayLocale, allowDecimal));
    }
    onBlur?.(e);
  };

  return (
    <Input
      type="text"
      inputMode={inputMode}
      value={displayValue}
      onChange={handleChange}
      onPaste={handlePaste}
      onBlur={handleBlur}
      {...rest}
    />
  );
}
